import React, { ChangeEventHandler, useMemo } from 'react';
import { Flex, Form, Input, Progress, Tooltip } from '@app/ui';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormValues } from '@app/settings/hooks/usePasswordForm';

type PasswordFormProps = {
    percent: number;
    passwordStatus: 'exception' | 'active';
    formInstance: FormikProps<FormValues>;
    checkPasswordStrength: (
        change: FormikProps<FormValues>['handleChange'],
    ) => ChangeEventHandler;
};

export const PasswordForm = ({
    formInstance,
    checkPasswordStrength,
    percent,
    passwordStatus,
}: PasswordFormProps) => {
    const { t } = useTranslation('settings');
    const { values, errors, touched, handleChange, handleBlur } = formInstance;
    const handlePasswordChange = checkPasswordStrength(handleChange);

    const passwordError = !!(touched.password && errors.password);
    const repeatPasswordError = !!(
        touched.repeatPassword && errors.repeatPassword
    );
    const newPasswordError = !!(touched.newPassword && errors.newPassword);

    const TooltipTitle = useMemo(
        () => (
            <div>
                <p>{t('userSettings.password.form.tooltip.title')}</p>
                <ul>
                    <li>* {t('userSettings.password.form.tooltip.length')}</li>
                    <li>
                        * {t('userSettings.password.form.tooltip.uppercase')}
                    </li>
                    <li>
                        * {t('userSettings.password.form.tooltip.lowercase')}
                    </li>
                    <li>* {t('userSettings.password.form.tooltip.symbol')}</li>
                    <li>
                        * {t('userSettings.password.form.tooltip.numerical')}
                    </li>
                    <li>* {t('userSettings.password.form.tooltip.match')}</li>
                </ul>
            </div>
        ),
        [t],
    );

    return (
        <Flex vertical className="-form">
            <form>
                <Form.Item
                    layout="vertical"
                    className="-m-b-3"
                    label={t('userSettings.password.form.existingPassword')}
                    validateStatus={passwordError ? 'error' : undefined}
                    help={
                        passwordError && typeof errors.password === 'string'
                            ? errors.password
                            : undefined
                    }
                >
                    <Input.Password
                        type="password"
                        name="password"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                    />
                </Form.Item>

                <Form.Item
                    layout="vertical"
                    label={t('userSettings.password.form.newPassword')}
                    validateStatus={newPasswordError ? 'error' : undefined}
                    help={
                        newPasswordError &&
                        typeof errors.newPassword === 'string'
                            ? errors.newPassword
                            : undefined
                    }
                >
                    <Input.Password
                        type="password"
                        name="newPassword"
                        onChange={handlePasswordChange}
                        onBlur={handleBlur}
                        value={values.newPassword}
                    />
                </Form.Item>

                <Form.Item
                    layout="vertical"
                    label={t('userSettings.password.form.reEnterNewPassword')}
                    validateStatus={repeatPasswordError ? 'error' : undefined}
                    help={
                        repeatPasswordError &&
                        typeof errors.repeatPassword === 'string'
                            ? errors.repeatPassword
                            : undefined
                    }
                >
                    <Input.Password
                        type="password"
                        name="repeatPassword"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.repeatPassword}
                    />
                </Form.Item>

                <Tooltip
                    styles={{ root: { maxWidth: 'unset' } }}
                    title={TooltipTitle}
                >
                    <Form.Item
                        layout="vertical"
                        className="-m-t-3"
                        label={t('userSettings.password.form.passwordStrength')}
                    >
                        <Progress
                            percent={percent}
                            showInfo={!!passwordStatus}
                            status={passwordStatus}
                        />
                    </Form.Item>
                </Tooltip>
            </form>
        </Flex>
    );
};

export default PasswordForm;
